import {
  Badge,
  Box,
  Button,
  Checkbox,
  Divider,
  Flex,
  Grid,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Tab,
  TabList,
  Tabs,
  Text,
  useToast,
} from '@chakra-ui/react';
import { Document, DocumentType } from '@pelicargo/types';
import { Show } from '@pelicargo/ui';
import { modelUtils, prettyNumber, StructuredContact, toCents, toDollars } from '@pelicargo/utils';
import { shipmentFormPaymentOptions } from '@pelicargo/utils';
import { ShipmentFormPayment } from '@prisma/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { HiChevronLeft, HiChevronRight, HiPlus, HiXMark } from 'react-icons/hi2';
import { useParams } from 'react-router-dom';

import { SimpleForm } from '../../components/SimpleForm';
import { SimpleInput } from '../../components/SimpleInput';
import { SimpleSelect } from '../../components/SimpleSelect';
import { SimpleTextarea } from '../../components/SimpleTextarea';
import { StructuredContactInput } from '../../components/StructuredContactInput';
import { trpc, TrpcOutputs } from '../../config/trpc';
import { AwbUploadCard } from './AwbUploadCard';

type ShipmentKind = 'MAWB' | 'HAWB';

type ShipmentFormDraft = {
  // id of the backing ShipmentForm record; absent for not-yet-created forms.
  id?: number;
  kind: ShipmentKind;
  label: string;
  shipper_contact: StructuredContact;
  consignee_contact: StructuredContact;
  notify_contact: StructuredContact;
  handling_instructions: string;
  payment: ShipmentFormPayment;
  accounting_info: string;
  target_rate: string;
  nature_of_goods: string;
  hs_codes: string;
  itn_number: string;
  document_id: number | null;
  selected_cargo_ids: string[];
};

// Subset of the ShipmentForm relation returned by getFinalQuote that we hydrate from.
type SavedShipmentForm = {
  id: number;
  parent_shipment_form_id: number | null;
  shipper_contact: StructuredContact | null;
  consignee_contact: StructuredContact | null;
  notify_contact: StructuredContact | null;
  handling_instructions: string | null;
  payment: ShipmentFormPayment;
  accounting_info: string | null;
  target_rate: number | null;
  nature_of_goods: string | null;
  hs_code: string | null;
  itn_number: string | null;
  documents?: { id: number }[];
  cargo?: { id: number }[];
};

type FormValues = {
  shipment_forms: ShipmentFormDraft[];
};

export type AwbInstructionVariant = 'direct' | 'mawb-hawb';

type Props = {
  quote?: TrpcOutputs['getFinalQuote']['quote'];
  shipmentForms?: TrpcOutputs['getShipmentFormsByRequestId'];
  isOpen: boolean;
  flowIsOpen: boolean;
  onClose: () => void;
  onBack: () => void;
  onAdditionalDocuments?: () => void;
  hawbCount: number;
  variant?: AwbInstructionVariant;
  mode: 'update' | 'notify';
  // ITN pre-entered earlier in the booking flow; seeds the MAWB when it has none.
  initialItnNumber?: string;
};

const createShipmentForms = (hawbCount: number): ShipmentFormDraft[] => {
  const safeHawbCount = Math.max(0, Math.floor(hawbCount));

  return [
    createEmptyMawbForm(),
    ...Array.from({ length: safeHawbCount }).map((_, idx) => createEmptyHawbForm(idx + 1)),
  ];
};

const createEmptyMawbForm = (): ShipmentFormDraft => ({
  kind: 'MAWB',
  label: 'MAWB 1',
  shipper_contact: {},
  consignee_contact: {},
  notify_contact: {},
  handling_instructions: '',
  payment: 'PRE_PAID',
  accounting_info: '',
  target_rate: '',
  nature_of_goods: '',
  hs_codes: '',
  itn_number: '',
  document_id: null,
  selected_cargo_ids: [],
});

const createEmptyHawbForm = (index: number): ShipmentFormDraft => ({
  kind: 'HAWB',
  label: `HAWB ${index}`,
  shipper_contact: {},
  consignee_contact: {},
  notify_contact: {},
  handling_instructions: '',
  payment: 'PRE_PAID',
  accounting_info: '',
  target_rate: '',
  nature_of_goods: '',
  hs_codes: '',
  itn_number: '',
  document_id: null,
  selected_cargo_ids: [],
});

const normalizeShipmentForms = (forms: ShipmentFormDraft[]): ShipmentFormDraft[] => {
  if (!forms.length) return [createEmptyMawbForm()];

  return forms.map((form, index) => {
    if (index === 0) {
      return { ...form, kind: 'MAWB', label: 'MAWB 1' };
    }

    return { ...form, kind: 'HAWB', label: `HAWB ${index}` };
  });
};

const toDraft = (form: SavedShipmentForm): ShipmentFormDraft => ({
  id: form.id,
  kind: form.parent_shipment_form_id ? 'HAWB' : 'MAWB',
  label: '',
  shipper_contact: form.shipper_contact ?? {},
  consignee_contact: form.consignee_contact ?? {},
  notify_contact: form.notify_contact ?? {},
  handling_instructions: form.handling_instructions ?? '',
  payment: form.payment === ShipmentFormPayment.COLLECT ? ShipmentFormPayment.COLLECT : ShipmentFormPayment.PRE_PAID,
  accounting_info: form.accounting_info ?? '',
  // `target_rate` is stored in cents; the field shows dollars (as the admin form does).
  target_rate: form.target_rate != null ? String(toDollars(form.target_rate)) : '',
  nature_of_goods: form.nature_of_goods ?? '',
  hs_codes: form.hs_code ?? '',
  itn_number: form.itn_number ?? '',
  document_id: form.documents?.[0]?.id ?? null,
  selected_cargo_ids: form.cargo?.map((cargo) => String(cargo.id)) ?? [],
});

// Hydrate the form drafts from the saved ShipmentForm records: the top-level
// record is the MAWB, its children are the HAWBs (ordered).
const mapSavedShipmentForms = (
  shipmentForms: SavedShipmentForm[] | null | undefined,
): ShipmentFormDraft[] | null => {
  const ordered = modelUtils.shipmentForms.orderMawbFirst(shipmentForms);
  if (!ordered.length) return null;

  return normalizeShipmentForms(ordered.map(toDraft));
};

const resolveInitialShipmentForms = (
  shipmentForms: SavedShipmentForm[] | null | undefined,
  hawbCount: number,
): ShipmentFormDraft[] => {
  return mapSavedShipmentForms(shipmentForms) ?? createShipmentForms(hawbCount);
};

/**
 * Whether a parsed contact holds anything at all.
 *
 * Recurses, because `address` is a nested object on `StructuredContact`: a shallow
 * string-only scan reads an address-only paste (every scalar nullish, `address`
 * populated) as empty, which then fails the save gate while the operator is looking
 * at the address they just pasted.
 */
const hasContact = (contact?: StructuredContact | null): boolean => {
  if (!contact || typeof contact !== 'object') return false;
  return Object.values(contact).some((value) => {
    if (typeof value === 'string') return value.trim().length > 0;
    return hasContact(value as StructuredContact | null);
  });
};

const hasAnyFormInput = (form: ShipmentFormDraft) => {
  return (
    !!form.document_id ||
    hasContact(form.shipper_contact) ||
    hasContact(form.consignee_contact) ||
    hasContact(form.notify_contact) ||
    !!form.handling_instructions?.trim() ||
    !!form.accounting_info?.trim() ||
    !!form.itn_number?.trim() ||
    !!form.target_rate?.trim() ||
    !!form.nature_of_goods?.trim() ||
    !!form.hs_codes?.trim() ||
    (form.selected_cargo_ids?.length ?? 0) > 0
  );
};

export const ShipmentInstructionsModal = ({
  quote,
  shipmentForms: initialShipmentForms,
  isOpen,
  flowIsOpen,
  onClose,
  onBack,
  onAdditionalDocuments,
  hawbCount,
  variant = 'mawb-hawb',
  mode,
  initialItnNumber,
}: Props) => {
  const toast = useToast({ duration: 3000, isClosable: true, position: 'bottom' });
  const { quoteId } = useParams<{ quoteId: string }>();
  const trpcUtils = trpc.useUtils();
  const { mutateAsync: updateDocuments, isLoading } = trpc.updateDocuments.useMutation();
  const { mutateAsync: createShipmentForm } = trpc.createShipmentForm.useMutation();
  const { mutateAsync: updateShipmentForm } = trpc.updateShipmentForm.useMutation();
  const { mutateAsync: deleteShipmentForm } = trpc.deleteShipmentForm.useMutation();
  const [isSaving, setIsSaving] = useState(false);
  const [isNotifyLoading, setIsNotifyLoading] = useState(false);
  const [activeHawbTab, setActiveHawbTab] = useState(0);
  const [showAllFieldsByFormIndex, setShowAllFieldsByFormIndex] = useState<Record<number, boolean>>({});
  // Drives the Direct vs. consolidated layout. Seeded from the requested variant
  // so the first paint is already the right layout, then toggled by add/remove
  // HAWB - never derived from a stale watched form array.
  const [isDirectLayout, setIsDirectLayout] = useState(variant === 'direct');
  const initializedVariant = useRef<AwbInstructionVariant | null>(null);
  // True while the sole HAWB's cargo selection is the one we filled in rather
  // than the user's own choice. Cleared as soon as they touch a checkbox.
  const didAutoSelectSoleHawbCargo = useRef(false);
  // The persisted forms the drafts are hydrated from. `initialShipmentForms` is the live
  // getShipmentFormsByRequestId result; `quote.shipment_forms` rides along on the cached
  // getFinalQuote. Both the hydrate below and the delete-diff in `saveShipmentForms` must
  // read this one source — diffing against the *other* list lets a HAWB the user removed
  // survive the save (it is absent from the diff list, so it is never deleted) and keep
  // syncing to CargoWise.
  const persistedShipmentForms = useMemo(
    () => initialShipmentForms ?? quote?.shipment_forms,
    [initialShipmentForms, quote?.shipment_forms],
  );

  const initialForms = useMemo(() => {
    const forms = resolveInitialShipmentForms(persistedShipmentForms, hawbCount);
    let resolved: ShipmentFormDraft[];
    // The direct layout shows only the MAWB; collapse any saved/extra HAWB forms
    // so an explicit "Direct AWB" choice always renders the direct layout.
    if (variant === 'direct') {
      resolved = normalizeShipmentForms(forms.slice(0, 1));
    } else if (variant === 'mawb-hawb' && forms.length <= 1) {
      // The consolidated (multiple) layout should always start with at least one
      // HAWB present, even if saved instructions only contained the MAWB.
      resolved = normalizeShipmentForms([...forms, createEmptyHawbForm(1)]);
    } else {
      resolved = forms;
    }

    // Seed the MAWB's ITN from the value collected earlier in the booking flow,
    // but never override an ITN already saved on the form.
    if (initialItnNumber && resolved[0] && !resolved[0].itn_number) {
      resolved = [{ ...resolved[0], itn_number: initialItnNumber }, ...resolved.slice(1)];
    }
    return resolved;
  }, [persistedShipmentForms, hawbCount, variant, initialItnNumber]);

  const methods = useForm<FormValues>({
    mode: 'onSubmit',
    defaultValues: {
      shipment_forms: initialForms,
    },
  });

  // (Re)initialize the form whenever the modal opens or the requested variant
  // changes. Keying on the variant (not just a once-per-open flag) ensures that
  // if isOpen is applied before the variant settles, the form still re-resets to
  // the correct layout instead of keeping stale forms from the previous variant.
  useEffect(() => {
    if (!isOpen) return;
    if (initializedVariant.current === variant) return;
    methods.reset({ shipment_forms: initialForms });
    setActiveHawbTab(0);
    setIsDirectLayout(variant === 'direct');
    didAutoSelectSoleHawbCargo.current = false;
    setShowAllFieldsByFormIndex({});
    initializedVariant.current = variant;
  }, [initialForms, isOpen, variant, methods]);

  useEffect(() => {
    if (!isOpen) {
      initializedVariant.current = null;
    }
  }, [isOpen]);

  useEffect(() => {
    if (!flowIsOpen) {
      initializedVariant.current = null;
    }
  }, [flowIsOpen]);

  const shipmentForms = methods.watch('shipment_forms') || [];
  const hawbForms = shipmentForms.slice(1);
  const activeHawbIndex = Math.max(0, Math.min(activeHawbTab, Math.max(0, hawbForms.length - 1)));
  const activeHawbFormIndex = Math.max(1, activeHawbIndex + 1);
  const showDirectLayout = isDirectLayout;

  const handleAddHawb = () => {
    const currentForms = methods.getValues('shipment_forms') || [];
    // From the direct layout, "Add HAWB" enters the consolidated layout with a
    // single HAWB, so start from just the MAWB and ignore any stale HAWB drafts.
    // From the consolidated layout it appends an additional HAWB.
    const baseForms = showDirectLayout ? currentForms.slice(0, 1) : currentForms;
    // With a second HAWB the cargo has to be shared out, so hand back the
    // selection we made while there was only one. The user's own picks stay put -
    // and cargo held by one HAWB is locked on the others.
    const releasedForms = didAutoSelectSoleHawbCargo.current
      ? baseForms.map((form, index) => (index === 1 ? { ...form, selected_cargo_ids: [] } : form))
      : baseForms;
    didAutoSelectSoleHawbCargo.current = false;
    // Once there are houses the goods are declared on them, and the MAWB stops
    // showing these fields - so carry the direct MAWB's entry over to the first
    // HAWB rather than saving a copy the user can no longer see or edit.
    const carriedNature = showDirectLayout ? releasedForms[0]?.nature_of_goods : '';
    const carriedHsCodes = showDirectLayout ? releasedForms[0]?.hs_codes : '';
    const clearedForms = showDirectLayout
      ? releasedForms.map((form, index) => (index === 0 ? { ...form, nature_of_goods: '', hs_codes: '' } : form))
      : releasedForms;
    const nextHawbNumber = Math.max(1, clearedForms.length);
    const nextHawb = {
      ...createEmptyHawbForm(nextHawbNumber),
      nature_of_goods: carriedNature || '',
      hs_codes: carriedHsCodes || '',
    };
    const nextForms = normalizeShipmentForms([...clearedForms, nextHawb]);
    methods.setValue('shipment_forms', nextForms, { shouldDirty: true });
    setActiveHawbTab(nextForms.length - 2);
    // Adding a HAWB converts the direct layout into the consolidated one.
    setIsDirectLayout(false);
  };

  const handleRemoveHawb = (hawbIndex: number) => {
    const currentForms = methods.getValues('shipment_forms') || [];
    const formIndexToRemove = hawbIndex + 1;

    if (formIndexToRemove <= 0 || formIndexToRemove >= currentForms.length) return;

    const nextForms = normalizeShipmentForms(currentForms.filter((_, index) => index !== formIndexToRemove));
    methods.setValue('shipment_forms', nextForms, { shouldDirty: true });

    const nextHawbCount = Math.max(0, nextForms.length - 1);
    // Removing the last HAWB on a direct shipment returns to the direct layout.
    if (nextHawbCount === 0 && variant === 'direct') {
      setIsDirectLayout(true);
    }
    setActiveHawbTab((prev) => {
      if (nextHawbCount === 0) return 0;
      if (prev > hawbIndex) return prev - 1;
      if (prev === hawbIndex) return Math.max(0, Math.min(hawbIndex, nextHawbCount - 1));
      return Math.min(prev, nextHawbCount - 1);
    });
  };

  const cargoRows = useMemo(() => {
    return (quote?.request?.cargo || []).map((cargo, idx) => ({
      id: String(cargo?.id ?? idx + 1),
      pieces: Number(cargo?.quantity || 0),
      dimensions: `${prettyNumber(cargo?.length || 0)}x${prettyNumber(cargo?.width || 0)}x${prettyNumber(cargo?.height || 0)}`,
      weight: Number(cargo?.weight || 0),
    }));
  }, [quote?.request?.cargo]);

  // A single HAWB carries the whole shipment, so tick every cargo line for it
  // instead of making the user do it. Only fills an empty selection, so a saved
  // or deliberately narrowed selection is never overwritten.
  useEffect(() => {
    if (!isOpen || hawbForms.length !== 1 || !cargoRows.length) return;
    if ((methods.getValues('shipment_forms.1.selected_cargo_ids') || []).length) return;

    methods.setValue(
      'shipment_forms.1.selected_cargo_ids',
      cargoRows.map((cargo) => cargo.id),
      { shouldDirty: true },
    );
    didAutoSelectSoleHawbCargo.current = true;
  }, [isOpen, hawbForms.length, cargoRows, methods]);

  const requestDocuments = useMemo(() => quote?.request?.documents || [], [quote?.request?.documents]);

  const getDocumentForForm = (formIndex: number): Partial<Document> | null => {
    const documentId = shipmentForms[formIndex]?.document_id;
    if (!documentId) return null;

    return requestDocuments.find((document) => document.id === documentId) ?? null;
  };

  const hasUploadedDocument = (formIndex: number) => !!shipmentForms[formIndex]?.document_id;

  const handleDocumentChange = (formIndex: number) => async (document: Partial<Document> | null) => {
    methods.setValue(`shipment_forms.${formIndex}.document_id`, document?.id ?? null, { shouldDirty: true });

    if (document?.id && quoteId) {
      await trpcUtils.getFinalQuote.invalidate({ id: Number(quoteId) });
    }
  };

  const cardStyle = {
    borderWidth: '1px',
    borderColor: 'border.light',
    borderRadius: 'md',
    p: '2',
    bg: 'bg.surface',
  } as const;

  // Collected on whichever waybill actually declares the goods: the MAWB on a
  // direct shipment, and each HAWB once there are houses - never both.
  const renderNatureAndHsFields = (index: number) => {
    if (hasUploadedDocument(index)) return null;

    return (
      <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="2">
        <SimpleInput<any>
          name={`shipment_forms.${index}.nature_of_goods`}
          label="Nature of Goods"
          placeholder="Nature of goods"
          variant="outline"
          p="2"
          isRequired
        />
        <SimpleInput<any>
          name={`shipment_forms.${index}.hs_codes`}
          label="HS Code(s)"
          placeholder="HS code(s)"
          variant="outline"
          p="2"
        />
      </Grid>
    );
  };

  const renderDirectLayout = () => (
    <Stack spacing="3.5">
      <Flex align="center" justify="space-between" py="1">
        <Text fontSize="2xl" fontWeight="semibold">
          Direct MAWB
        </Text>
        <Button size="sm" variant="outline" colorScheme="gray" onClick={handleAddHawb}>
          Add HAWB
        </Button>
      </Flex>

      <AwbUploadCard
        key="direct-mawb-upload"
        label="mawb"
        requestId={quote?.request?.id}
        document={getDocumentForForm(0)}
        onDocumentChange={handleDocumentChange(0)}
        showFormHint={!hasUploadedDocument(0)}
      />
      {renderFormColumn(0)}
      {renderNatureAndHsFields(0)}
    </Stack>
  );

  const renderConsolidatedLayout = () => (
    <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={{ base: '4', md: '3.5' }} alignItems="start">
      <Stack spacing="2.5" minW={0}>
        <Stack direction="row" align="start" justify="space-between" py="1">
          <Text fontSize="2xl" fontWeight="semibold">
            MAWB
          </Text>
        </Stack>
        <AwbUploadCard
          key="consolidated-mawb-upload"
          label="mawb"
          requestId={quote?.request?.id}
          document={getDocumentForForm(0)}
          onDocumentChange={handleDocumentChange(0)}
          showFormHint={!hasUploadedDocument(0)}
        />
        {renderFormColumn(0)}
      </Stack>

      <Stack
        spacing="2.5"
        minW={0}
        pl={{ md: '3.5' }}
        borderLeftWidth={{ md: '1px' }}
        borderLeftColor={{ md: 'gray.200' }}
      >
        {renderHawbTabs()}

        {hawbForms.length > 0 ? (
          <>
            <AwbUploadCard
              key={`hawb-upload-${activeHawbFormIndex}`}
              label={`HAWB ${activeHawbIndex + 1}`}
              requestId={quote?.request?.id}
              document={getDocumentForForm(activeHawbFormIndex)}
              onDocumentChange={handleDocumentChange(activeHawbFormIndex)}
              showFormHint={!hasUploadedDocument(activeHawbFormIndex)}
            />
            {renderFormColumn(activeHawbFormIndex)}
            {renderNatureAndHsFields(activeHawbFormIndex)}
            {renderCargoSelection()}
          </>
        ) : (
          <Box {...cardStyle}>
            <Text fontSize="xs" color="text.subtle">
              No HAWBs configured.
            </Text>
          </Box>
        )}
      </Stack>
    </Grid>
  );

  const renderFormColumn = (index: number) => {
    if (hasUploadedDocument(index)) return null;
    const showAllFields = showAllFieldsByFormIndex[index];

    return (
      <Stack spacing="2.5">
        <StructuredContactInput
          key={`shipper-${index}`}
          name={`shipment_forms.${index}.shipper_contact`}
          type="Contact"
          label="Shipper Details"
          placeholder="Paste shipper name, address, phone, email…"
          rows={10}
          variant="ghost"
        />
        <StructuredContactInput
          key={`consignee-${index}`}
          name={`shipment_forms.${index}.consignee_contact`}
          type="Contact"
          label="Consignee Details"
          placeholder="Paste consignee name, address, phone, email…"
          rows={10}
          variant="ghost"
        />

        <Button
          size="xs"
          variant="ghost"
          colorScheme="gray"
          alignSelf="center"
          onClick={() =>
            setShowAllFieldsByFormIndex((prev) => ({
              ...prev,
              [index]: !prev[index],
            }))
          }
        >
          {showAllFieldsByFormIndex[index] ? 'Hide additional fields' : 'Show additional fields'}
        </Button>

        <Show if={showAllFields}>
          <>
            <StructuredContactInput
              key={`notify-${index}`}
              name={`shipment_forms.${index}.notify_contact`}
              type="Contact"
              label="Notify Details"
              placeholder="Paste notify party name, address, phone, email…"
              rows={6}
              variant="ghost"
            />
            <SimpleTextarea<any>
              key={`handling-${index}`}
              name={`shipment_forms.${index}.handling_instructions`}
              label="Handling Instructions"
              placeholder="Handling instructions"
              rows={6}
              variant="outline"
              p="2"
            />
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap="2">
              <SimpleSelect<any>
                key={`payment-${index}`}
                name={`shipment_forms.${index}.payment`}
                label="Payment"
                placeholder="Select payment"
                options={shipmentFormPaymentOptions}
                variant="outline"
                p="2"
              />
              <SimpleInput<any>
                key={`rate-${index}`}
                name={`shipment_forms.${index}.target_rate`}
                label="Rate"
                placeholder="Rate"
                type="number"
                variant="outline"
                p="2"
              />
            </Grid>
            <SimpleInput<any>
              key={`accounting-${index}`}
              name={`shipment_forms.${index}.accounting_info`}
              label="Accounting Info"
              placeholder="Accounting info"
              variant="outline"
              p="2"
            />
          </>
        </Show>

        <Show if={index === 0}>
          <SimpleInput<any>
            key={`itn-${index}`}
            name={`shipment_forms.${index}.itn_number`}
            label="ITN Number (if available)"
            placeholder="XYYYYMMDD123456"
            variant="outline"
            p="2"
          />
        </Show>
      </Stack>
    );
  };

  const renderHawbTabs = () => (
    <Tabs index={activeHawbIndex} onChange={setActiveHawbTab} variant="line" colorScheme="primary" flex="1" minW={0}>
      <Box overflowX="auto">
        <TabList whiteSpace="nowrap" minW="max-content" alignItems="center" gap="1">
          {hawbForms.map((_form, index) => (
            <Tab key={`hawb-${index}`}>
              <HStack spacing="2">
                <Text>{`HAWB ${index + 1}`}</Text>
                <IconButton
                  className="remove-hawb-icon"
                  icon={<HiXMark />}
                  aria-label={`Remove HAWB ${index + 1}`}
                  size="2xs"
                  borderRadius="full"
                  color="text.muted"
                  variant="ghost"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    handleRemoveHawb(index);
                  }}
                />
              </HStack>
            </Tab>
          ))}
          <IconButton
            icon={<HiPlus />}
            aria-label="Add HAWB"
            size="xs"
            color="text.muted"
            variant="ghost"
            borderRadius="full"
            onClick={handleAddHawb}
          />
        </TabList>
      </Box>
    </Tabs>
  );

  const renderCargoSelection = () => {
    const hasDoc = hasUploadedDocument(activeHawbFormIndex);
    if (hasDoc) return null;

    const cargoOwnerById: Record<string, number> = {};

    shipmentForms.forEach((form, formIndex) => {
      if (formIndex === 0) return;
      (form.selected_cargo_ids || []).forEach((cargoId) => {
        if (cargoOwnerById[cargoId] === undefined) {
          cargoOwnerById[cargoId] = formIndex;
        }
      });
    });

    const selectedInActiveForm = methods.watch(`shipment_forms.${activeHawbFormIndex}.selected_cargo_ids`) || [];

    return (
      <Stack spacing="1.5">
        <Text fontSize="2xs" fontWeight="semibold" textTransform="uppercase" color="text.subtle" letterSpacing="0.04em">
          Select cargo belonging to this shipment
        </Text>
        {cargoRows.length > 0 ? (
          cargoRows.map((cargo) => {
            const ownerFormIndex = cargoOwnerById[cargo.id];
            const isOwnedByOtherForm = ownerFormIndex !== undefined && ownerFormIndex !== activeHawbFormIndex;
            const isChecked = selectedInActiveForm.includes(cargo.id) || isOwnedByOtherForm;
            const ownerLabel =
              ownerFormIndex !== undefined
                ? shipmentForms[ownerFormIndex]?.label || `HAWB ${ownerFormIndex}`
                : undefined;
            const showOwnerLabel = !!ownerLabel;

            return (
              <Flex
                key={cargo.id}
                align="center"
                justify="space-between"
                p="1.5"
                gap="2"
                opacity={isOwnedByOtherForm ? 0.65 : 1}
              >
                <HStack align="center" spacing="1.5" minW={0}>
                  <Checkbox
                    size="sm"
                    colorScheme="primary"
                    isChecked={isChecked}
                    isDisabled={isOwnedByOtherForm}
                    onChange={(event) => {
                      const next = event.target.checked
                        ? [...selectedInActiveForm, cargo.id]
                        : selectedInActiveForm.filter((id: string) => id !== cargo.id);
                      methods.setValue(`shipment_forms.${activeHawbFormIndex}.selected_cargo_ids`, next);
                      if (activeHawbFormIndex === 1) didAutoSelectSoleHawbCargo.current = false;
                    }}
                  />
                  <Text fontSize="2xs" color="text.paragraph" noOfLines={1}>
                    {cargo.pieces} Pcs | {cargo.dimensions} | {prettyNumber(cargo.weight)} kg
                  </Text>
                </HStack>
                <Show if={showOwnerLabel}>
                  <Badge colorScheme="gray" fontSize="2xs" px="1.5" py="0.5">
                    {ownerLabel}
                  </Badge>
                </Show>
              </Flex>
            );
          })
        ) : (
          <Text fontSize="xs" color="text.subtle">
            No cargo lines available for selection.
          </Text>
        )}
      </Stack>
    );
  };

  const saveShipmentForms = async (values: FormValues) => {
    const formEntries = values.shipment_forms.map((form, index) => {
      // Auto-selected cargo alone must not turn an untouched HAWB placeholder into a saved form.
      const isAutoSelected = index === 1 && didAutoSelectSoleHawbCargo.current;
      const hasInput = hasAnyFormInput(isAutoSelected ? { ...form, selected_cargo_ids: [] } : form);
      return { form, index, hasInput };
    });
    const hasHawbToSave = formEntries.some(({ form, index, hasInput }) => index > 0 && (!!form.id || hasInput));
    const formsToSave = formEntries
      .filter(({ form, index, hasInput }) => !!form.id || hasInput || (index === 0 && hasHawbToSave))
      .map(({ form }) => form);

    // Advancing without adding any shipment-form details is a valid no-op.
    if (!formsToSave.length) return true;

    const requestId = quote?.request?.id;
    if (!quoteId || !requestId) {
      toast({ title: 'Unable to save: missing quote or request.', status: 'error' });
      return false;
    }

    // Map a draft to the ShipmentForm create/update payload.
    const toPayload = (form: ShipmentFormDraft, parentShipmentFormId?: number) => ({
      quote_id: Number(quoteId),
      request_id: Number(requestId),
      payment: form.payment,
      shipper_contact: hasContact(form.shipper_contact) ? form.shipper_contact : null,
      consignee_contact: hasContact(form.consignee_contact) ? form.consignee_contact : null,
      notify_contact: hasContact(form.notify_contact) ? form.notify_contact : null,
      handling_instructions: form.handling_instructions,
      accounting_info: form.accounting_info,
      // Dollars in the field, cents in the column — see `toDraft` for the read side.
      target_rate: form.target_rate ? toCents(Number(form.target_rate)) : undefined,
      nature_of_goods: form.nature_of_goods,
      hs_code: form.hs_codes,
      itn_number: form.itn_number,
      documents: form.document_id ? [form.document_id] : [],
      cargo: form.selected_cargo_ids.map(Number),
      parent_shipment_form_id: parentShipmentFormId,
    });

    const upsert = (form: ShipmentFormDraft, parentShipmentFormId?: number) =>
      form.id
        ? updateShipmentForm({ id: form.id, ...toPayload(form, parentShipmentFormId) })
        : createShipmentForm(toPayload(form, parentShipmentFormId));

    try {
      setIsSaving(true);

      const [mawb, ...hawbs] = formsToSave;
      if (!mawb) {
        toast({ title: 'Unable to save shipment forms: missing MAWB form.', status: 'error' });
        return false;
      }

      // The MAWB is created/updated first so its id can parent the HAWBs.
      const savedMawb = await upsert(mawb);
      const savedHawbIds: number[] = [];
      for (const hawb of hawbs) {
        const saved = await upsert(hawb, savedMawb.id);
        savedHawbIds.push(saved.id);
      }

      // Delete ShipmentForm records that the user removed from the UI.
      const keptIds = new Set<number>([savedMawb.id, ...savedHawbIds]);
      const removedIds = (persistedShipmentForms ?? []).map((form) => form.id).filter((id) => !keptIds.has(id));
      for (const id of removedIds) {
        await deleteShipmentForm({ id });
      }

      await trpcUtils.getFinalQuote.invalidate({ id: Number(quoteId) });

      if (mode === 'notify') {
        setIsNotifyLoading(true);
        // Notify the FP about the AWB documents attached to the saved forms.
        // With no attached documents there is nothing to notify about, so the
        // backend safely skips the notification.
        const awbDocumentIds = formsToSave
          .map((form) => form.document_id)
          .filter((id): id is number => !!id);
        await updateDocuments({
          documents: awbDocumentIds.map((id) => ({ id, type: DocumentType.AIRWAY_BILL })),
          quote_id: Number(quoteId),
          shouldNotify: true,
        });
      }

      toast({
        title: mode === 'notify' ? 'Shipment forms updated and queued for notification.' : 'Shipment forms updated.',
        status: 'success',
      });

      return true;
    } catch {
      toast({
        title: 'Failed to save shipment forms.',
        status: 'error',
      });
      return false;
    } finally {
      setIsSaving(false);
      if (mode === 'notify') setIsNotifyLoading(false);
    }
  };

  const handleAdditionalDocuments = async () => {
    const values = methods.getValues();
    const didSave = await saveShipmentForms(values);
    if (!didSave) return;

    if (onAdditionalDocuments) {
      onAdditionalDocuments();
      return;
    }

    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} isCentered>
      <ModalOverlay />
      <ModalContent
        maxW={{ md: showDirectLayout ? 'xl' : '4xl' }}
        maxH={{ base: '100dvh', md: '90vh' }}
        borderRadius={{ base: '0', lg: 'md' }}
      >
        <ModalHeader pb="2" flexShrink={0}>
          AWB Instructions
        </ModalHeader>
        <SimpleForm onSubmit={() => undefined} submitOnBlur={false} fillHeight {...methods}>
          <ModalBody overflowY="auto" pb="2" flex="1" minH="0">
            <Stack spacing="3.5" pr={{ base: '0', md: '1' }}>
              <Text color="text.subtle" fontSize="sm">
                {showDirectLayout
                  ? 'Optional: add an AWB or invoice, or enter shipment details below.'
                  : 'Optional: add MAWB/HAWB documents or shipment details below.'}
              </Text>
              <Divider />

              {showDirectLayout ? renderDirectLayout() : renderConsolidatedLayout()}
            </Stack>
          </ModalBody>
          <ModalFooter flexShrink={0}>
            <Stack direction="row" justify="space-between" w="full">
              <Button size="sm" onClick={onBack} leftIcon={<HiChevronLeft />}>
                Back
              </Button>
              <Button
                colorScheme="primary"
                type="button"
                size="sm"
                onClick={handleAdditionalDocuments}
                isLoading={isLoading || isNotifyLoading || isSaving}
                rightIcon={<HiChevronRight />}
              >
                Additional Documents
              </Button>
            </Stack>
          </ModalFooter>
        </SimpleForm>
      </ModalContent>
    </Modal>
  );
};
